// SPIKE: Fathom connect + MCP roundtrip test page. Mirrors ConnectGranola.tsx;
// will collapse into a single generic ConnectProvider component once the
// framework lands.

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { startFathomConnect, testFathomConnection } from "../lib/api";
import { Crumbs } from "../components/Crumbs";
import { PageTitle } from "../components/PageTitle";

type TestState =
  | { kind: "idle" }
  | { kind: "running" }
  | { kind: "ok"; payload: unknown }
  | { kind: "error"; message: string };

export function ConnectFathom() {
  const [searchParams] = useSearchParams();
  const justConnected = searchParams.get("ok") === "true";
  const errorParam = searchParams.get("error");

  const [connectPending, setConnectPending] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [test, setTest] = useState<TestState>({ kind: "idle" });

  useEffect(() => {
    if (justConnected && test.kind === "idle") {
      void runTest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justConnected]);

  async function onConnect() {
    setConnectError(null);
    setConnectPending(true);
    try {
      const { authorizeUrl } = await startFathomConnect();
      window.location.href = authorizeUrl;
    } catch (err) {
      setConnectError(err instanceof Error ? err.message : "Could not start connect.");
      setConnectPending(false);
    }
  }

  async function runTest() {
    setTest({ kind: "running" });
    try {
      const payload = await testFathomConnection();
      setTest({ kind: "ok", payload });
    } catch (err) {
      setTest({ kind: "error", message: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <div style={{ maxWidth: 720 }}>
      <Crumbs trail={[{ label: "Settings", to: "/settings" }, "Fathom"]} />
      <PageTitle lede="Connect Fathom once. Klemento will pull a transcript on demand and coach you on it. Free tier accounts work; transcripts never leave the request that uses them.">
        Connect Fathom.
      </PageTitle>

      {errorParam ? <ErrorBox message={`Fathom callback returned an error: ${errorParam}`} /> : null}
      {justConnected ? (
        <div
          role="status"
          style={{
            background: "rgb(var(--color-success, 30 130 80) / 0.08)",
            border: "1px solid rgb(var(--color-success, 30 130 80) / 0.3)",
            color: "rgb(var(--color-ink))",
            padding: "10px 14px",
            borderRadius: 10,
            marginBottom: 14,
            fontSize: 14,
          }}
        >
          Fathom tokens stored. Running MCP roundtrip test below.
        </div>
      ) : null}
      {connectError ? <ErrorBox message={connectError} /> : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {justConnected ? (
          <Link to="/connectors/fathom/meetings" className="btn btn-primary" style={{ width: "100%", textDecoration: "none" }}>
            Browse your Fathom meetings &rarr;
          </Link>
        ) : (
          <button type="button" onClick={onConnect} disabled={connectPending} className="btn btn-primary" style={{ width: "100%" }}>
            {connectPending ? "Redirecting to Fathom..." : "Connect Fathom"}
          </button>
        )}

        {justConnected ? (
          <button type="button" onClick={onConnect} disabled={connectPending} className="btn btn-secondary" style={{ width: "100%" }}>
            {connectPending ? "Redirecting to Fathom..." : "Re-connect Fathom"}
          </button>
        ) : null}

        <button
          type="button"
          onClick={runTest}
          disabled={test.kind === "running"}
          className="btn btn-secondary"
          style={{ width: "100%" }}
        >
          {test.kind === "running" ? "Running MCP roundtrip..." : "Run MCP roundtrip test"}
        </button>
      </div>

      {test.kind === "ok" ? (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 8px" }}>MCP roundtrip OK</h2>
          <pre
            style={{
              background: "rgb(var(--color-surface-strong))",
              padding: 16,
              borderRadius: 10,
              fontSize: 12,
              overflow: "auto",
              maxHeight: 480,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(test.payload, null, 2)}
          </pre>
        </div>
      ) : null}

      {test.kind === "error" ? (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, margin: "0 0 8px", color: "rgb(var(--color-warning))" }}>
            MCP roundtrip failed
          </h2>
          <pre
            style={{
              background: "rgb(var(--color-warning) / 0.06)",
              border: "1px solid rgb(var(--color-warning) / 0.3)",
              padding: 16,
              borderRadius: 10,
              fontSize: 13,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {test.message}
          </pre>
        </div>
      ) : null}
    </div>
  );
}

function ErrorBox({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        background: "rgb(var(--color-warning) / 0.08)",
        border: "1px solid rgb(var(--color-warning) / 0.3)",
        color: "rgb(var(--color-warning))",
        padding: "10px 14px",
        borderRadius: 10,
        marginBottom: 14,
        fontSize: 14,
      }}
    >
      {message}
    </div>
  );
}
